<script setup lang="ts">
import type {
  ExternalCourse,
  ExternalSubject,
  SubjectRequest,
} from '@/models/current-student-advanced-standing-form/api'
import type {
  ArticulationCondition,
  ArticulationTableRow,
  CourseRuleOutcomeRow,
  PrecedentOutcome,
  SubjectPrecedentTableRow,
} from '@/models/current-student-advanced-standing-form/precedents'
import ExistingPrecedentsArticulationDisplay from '@/components/current-student-advanced-standing-form/existing-precedents/ExistingPrecedentsArticulationDisplay.vue'
import ExistingPrecedentsCourseRulesDisplay from '@/components/current-student-advanced-standing-form/existing-precedents/ExistingPrecedentsCourseRulesDisplay.vue'
import ExistingPrecedentsSubjectPrecedentsDisplay from '@/components/current-student-advanced-standing-form/existing-precedents/ExistingPrecedentsSubjectPrecedentsDisplay.vue'

defineProps<{
  externalCourses: ExternalCourse[]
  handbookUrl: string
  showSpgTool: boolean
  courseSubjects: (course: ExternalCourse) => ExternalSubject[]
  subjectPrecedentRows: (subject: ExternalSubject) => SubjectPrecedentTableRow[]
  subjectDisplayCode: (subject: ExternalSubject) => string
  outcomePriorityClass: (outcome: PrecedentOutcome) => string
  articulationRows: (course: ExternalCourse) => ArticulationTableRow[]
  articulationConditions: (course: ExternalCourse) => ArticulationCondition[]
  courseRuleOutcomeRows: (course: ExternalCourse) => CourseRuleOutcomeRow[]
}>()

const emit = defineEmits<{
  openSubjectRequest: [subject: ExternalSubject]
  removeSubjectRequest: [subject: ExternalSubject, subjectRequest: SubjectRequest]
}>()
</script>

<template>
  <fieldset>
    <legend>Existing Precedents</legend>
    <p>
      Below is a list of subjects that you have completed, each with the equivalent La Trobe subject.
      Subjects that have not been previously assessed by the University will appear as 'UNSPECIFIED CREDIT'.
      If the subjects that you would like to receive credit for are not shown, please add the subjects by clicking the 'Request different subject' button.
    </p>
    <p>
      It is important to understand that your course structure will limit the amount of credit you can receive, and the subjects shown below may not be compatible with your course.
      You should consult the <a class="HandbookLink" :href="handbookUrl" target="_blank">University Handbook</a>
      <span class="spgTool" :class="{ hidden: !showSpgTool }">
        or <a target="_blank" href="https://www.latrobe.edu.au/students/your-course/study-plan-guides" rel="noreferrer">Study Plan Guides tool</a>
      </span>
      to check what subjects you are required to undertake for your course and select only those.
      You can request as many subjects as you would like below, however, the University will decide which subjects are compatible with your course after your submission.
    </p>
    <ExistingPrecedentsSubjectPrecedentsDisplay
      :external-courses="externalCourses"
      :course-subjects="courseSubjects"
      :subject-precedent-rows="subjectPrecedentRows"
      :subject-display-code="subjectDisplayCode"
      :outcome-priority-class="outcomePriorityClass"
      @open-subject-request="emit('openSubjectRequest', $event)"
      @remove-subject-request="(subject, subjectRequest) => emit('removeSubjectRequest', subject, subjectRequest)"
    >
      <template #courseAfterSubjectPrecedents="{ course }">
        <ExistingPrecedentsArticulationDisplay
          :course="course"
          :articulation-rows="articulationRows"
          :articulation-conditions="articulationConditions"
        />

        <ExistingPrecedentsCourseRulesDisplay
          :course="course"
          :course-rule-outcome-rows="courseRuleOutcomeRows"
          :outcome-priority-class="outcomePriorityClass"
        />
      </template>
    </ExistingPrecedentsSubjectPrecedentsDisplay>
    <p>
      * Indicates a subject that you have specifically requested and will need academic approval.
    </p>
    <div class="alert alert-danger">
      <h3>Have you selected all the La Trobe subjects you want?</h3>
      <p>
        Please ensure the subject codes in the "La Trobe Code" column are the subjects you want to receive credit for.
        If there is an UNSPECIFIED CREDIT code listed (e.g. LTU115) this credit can only be used as an elective.
        If you want credit for a core subject please search the <a class="HandbookLink" :href="handbookUrl" target="_blank">University Handbook</a>
        for the desired subject and enter it above by clicking the "Request a different La Trobe subject" button.
      </p>
    </div>
    <div class="alert alert-danger" id="precedentsSaveErrorBox"></div>
  </fieldset>
</template>
